/*
  Global IntersectionObserver utility for one-time reveal on viewport entry.
  Exposes window.meIO.observeOnce(targets, options)
*/
(function () {
  if (!window.meIO) {
    window.meIO = {};
  }

  if (window.meIO.observeOnce) {
    return; // already defined
  }

  function toArray(targets) {
    if (!targets) return [];
    if (Array.isArray(targets)) return targets;
    if (NodeList.prototype.isPrototypeOf(targets)) return Array.from(targets);
    if (typeof targets === 'string') return Array.from(document.querySelectorAll(targets));
    return [targets];
  }

  function fallbackApply(elements, visibleClass) {
    elements.forEach(function (el) {
      el.classList.add(visibleClass);
    });
  }

  window.meIO.observeOnce = function observeOnce(targets, options) {
    const opts = options || {};
    const visibleClass = opts.visibleClass || 'is-io-visible';
    const threshold = typeof opts.threshold === 'number' ? opts.threshold : 0.2;
    const rootMargin = typeof opts.rootMargin === 'string' ? opts.rootMargin : '0px 0px -10% 0px';
    const root = opts.root || null;
    const onVisible = typeof opts.onVisible === 'function' ? opts.onVisible : null;

    const elements = toArray(targets);
    if (!elements.length) return function () {};

    if (!('IntersectionObserver' in window)) {
      fallbackApply(elements, visibleClass);
      return function () {};
    }

    const io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.classList.add(visibleClass);
          if (onVisible) onVisible(el);
          obs.unobserve(el);
        }
      });
    }, { root: root, rootMargin: rootMargin, threshold: threshold });

    elements.forEach(function (el) { io.observe(el); });
    return function () { io.disconnect(); };
  };
  
  if (!window.meIO.registerStaggeredOnce) {
    window.meIO.registerStaggeredOnce = function registerStaggeredOnce(containerOrSelector, itemSelector, options) {
      const opts = options || {};
      const container = typeof containerOrSelector === 'string'
        ? document.querySelector(containerOrSelector)
        : containerOrSelector;
      if (!container) return () => {};

      const threshold = typeof opts.threshold === 'number' ? opts.threshold : 0.2;
      const rootMargin = typeof opts.rootMargin === 'string' ? opts.rootMargin : '0px 0px -10% 0px';
      const visibleClass = opts.visibleClass || 'is-io-visible';
      const staggerStepMs = typeof opts.staggerStepMs === 'number' ? opts.staggerStepMs : 100;
      const staggerMaxMs = typeof opts.staggerMaxMs === 'number' ? opts.staggerMaxMs : 600;
      const sectionId = opts.sectionId; // optional: for shopify:section:load filter

      const apply = (rootEl) => {
        const items = Array.from(rootEl.querySelectorAll(itemSelector));
        if (!items.length) return;
        items.forEach((el, idx) => {
          const delay = Math.min(idx * staggerStepMs, staggerMaxMs);
          el.style.setProperty('--stagger', `${delay}ms`);
        });
        window.meIO.observeOnce(items, { threshold, rootMargin, visibleClass });
      };

      // initial
      apply(container);

      // re-apply on DOM updates within container
      const mo = new MutationObserver(() => {
        // throttle via rAF
        if (registerStaggeredOnce._raf) cancelAnimationFrame(registerStaggeredOnce._raf);
        registerStaggeredOnce._raf = requestAnimationFrame(() => apply(container));
      });
      mo.observe(container, { childList: true, subtree: true });

      // theme editor section reload support (optional)
      const sectionLoadHandler = (e) => {
        if (!e || !e.detail) return;
        if (sectionId && e.detail.sectionId !== String(sectionId)) return;
        apply(container);
      };
      document.addEventListener('shopify:section:load', sectionLoadHandler);

      // disposer
      return () => {
        mo.disconnect();
        document.removeEventListener('shopify:section:load', sectionLoadHandler);
      };
    };
  }
})();


